import { supabase } from '@/lib/supabase';
import { emitCoreActivity } from '@/lib/coreEventHooks';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 54. RATING / REVIEWS
// Infrastructure commune d'avis, partagée par tous les modules.
// Un avis est relié à : user/customer · business · service/product/booking
// (lorsque pertinent) · rating · comment · date · status.
// Prévoit : modération · signalement · réponse du professionnel.
//
// ⚠️ Les avis (opinions subjectives) doivent être DISTINGUÉS des données
// factuelles. Le champ `is_factual` marque les entrées de type « donnée
// factuelle » et le champ `is_verified` les avis liés à une commande réelle.
// ─────────────────────────────────────────────────────────────

export const REVIEW_ENTITY_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'business', label: 'Entreprise', icon: 'ri-store-2-line' },
  { key: 'service', label: 'Service', icon: 'ri-service-line' },
  { key: 'product', label: 'Produit', icon: 'ri-shopping-bag-line' },
  { key: 'booking', label: 'Réservation', icon: 'ri-hotel-line' },
  { key: 'professional', label: 'Professionnel', icon: 'ri-user-star-line' },
];

export const REVIEW_STATUSES: { key: string; label: string; className: string }[] = [
  { key: 'pending', label: 'En attente', className: 'bg-accent-100 text-accent-900' },
  { key: 'published', label: 'Publié', className: 'bg-primary-100 text-primary-900' },
  { key: 'rejected', label: 'Refusé', className: 'bg-red-100 text-red-800' },
  { key: 'hidden', label: 'Masqué', className: 'bg-background-100 text-foreground-500' },
];

export function reviewStatusMeta(key: string | null | undefined) {
  return REVIEW_STATUSES.find((s) => s.key === key) || REVIEW_STATUSES[0];
}

export function reviewEntityMeta(key: string | null | undefined) {
  return REVIEW_ENTITY_TYPES.find((e) => e.key === key) || REVIEW_ENTITY_TYPES[0];
}

export interface CoreReview {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  author_user_id: number | null;
  author_name: string | null;
  entity_type: string;
  entity_id: string | null;
  rating: number | null;
  title: string | null;
  comment: string | null;
  status: string | null;
  is_verified: boolean | null;
  is_factual: boolean | null;
  moderation_note: string | null;
  moderated_by: number | null;
  moderated_at: string | null;
  report_count: number | null;
  helpful_count: number | null;
  reply_body: string | null;
  reply_by: number | null;
  reply_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CoreReviewReport {
  id: number;
  review_id: number | null;
  organization_id: number | null;
  reporter_user_id: number | null;
  reporter_name: string | null;
  reason: string | null;
  detail: string | null;
  status: string | null;
  created_at: string | null;
}

export const REPORT_REASONS: { key: string; label: string }[] = [
  { key: 'contenu_abusif', label: 'Contenu abusif / insultant' },
  { key: 'faux_avis', label: 'Faux avis / spam' },
  { key: 'hors_sujet', label: 'Ne concerne pas la prestation' },
  { key: 'donnees_personnelles', label: 'Divulgue des données personnelles' },
  { key: 'autre', label: 'Autre' },
];

export async function listReviews(
  organizationId: number,
  filter?: { entityType?: string; entityId?: string; status?: string; limit?: number },
): Promise<CoreReview[]> {
  let query = supabase
    .from('core_reviews')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(filter?.limit ?? 200);
  if (filter?.entityType) query = query.eq('entity_type', filter.entityType);
  if (filter?.entityId) query = query.eq('entity_id', filter.entityId);
  if (filter?.status) query = query.eq('status', filter.status);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreReview[];
}

export interface CreateReviewInput {
  tenantId: number | null;
  organizationId: number;
  authorUserId?: number | null;
  authorName: string;
  entityType: string;
  entityId?: string | number | null;
  rating: number;
  title?: string | null;
  comment?: string | null;
  isVerified?: boolean;
  isFactual?: boolean;
  status?: string;
}

/** Crée un avis (status « pending » par défaut : la modération décide). */
export async function createReview(input: CreateReviewInput & { commerceId?: number | null }): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_reviews')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      author_user_id: input.authorUserId ?? null,
      author_name: input.authorName,
      entity_type: input.entityType,
      entity_id: input.entityId != null ? String(input.entityId) : null,
      rating: Math.max(1, Math.min(5, Math.round(input.rating))),
      title: input.title || null,
      comment: input.comment || null,
      status: input.status || 'pending',
      is_verified: !!input.isVerified,
      is_factual: !!input.isFactual,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  const id = (data?.id as number) ?? null;

  // Un avis créé directement « publié » déclenche aussi l’invalidation du cache
  // catalogue (préfixe review.), au même titre qu’une publication par modération.
  if (id && (input.status === 'published') && input.commerceId) {
    await emitReviewEvent({
      commerceId: input.commerceId,
      reviewId: id,
      eventKey: 'review.published',
      title: 'Avis publié',
      description: `Avis #${id} publié directement.`,
      actorUserId: input.authorUserId ?? null,
    });
  }
  return id;
}

/** Émet un événement avis du socle → invalidation automatique du cache (préfixe review.). */
async function emitReviewEvent(input: {
  commerceId: number;
  reviewId: number;
  eventKey: 'review.published' | 'review.moderated';
  title: string;
  description: string;
  actorUserId?: number | null;
}): Promise<void> {
  try {
    await emitCoreActivity({
      commerceId: input.commerceId,
      eventKey: input.eventKey,
      entityType: 'review',
      entityId: input.reviewId,
      title: input.title,
      description: input.description,
      actorUserId: input.actorUserId ?? null,
      source: 'reviews',
    });
  } catch {
    /* l’invalidation du cache ne doit jamais bloquer la modération */
  }
}

/** Modère un avis (publier / refuser / masquer).
 *  `commerceId` (users.id) → émet un événement review.* qui invalide
 *  automatiquement le cache catalogue/rapports/dashboard. */
export async function moderateReview(
  id: number,
  status: 'published' | 'rejected' | 'hidden' | 'pending',
  note?: string | null,
  moderatorUserId?: number | null,
  commerceId?: number | null,
): Promise<void> {
  const { error } = await supabase
    .from('core_reviews')
    .update({
      status,
      moderation_note: note || null,
      moderated_by: moderatorUserId ?? null,
      moderated_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) throw error;

  // Publication d’un avis → invalidation automatique du cache catalogue
  // (CACHE_INVALIDATION_MAP : préfixe « review. » → scopes dashboard + catalog + reports).
  if (commerceId) {
    await emitReviewEvent({
      commerceId,
      reviewId: id,
      eventKey: status === 'published' ? 'review.published' : 'review.moderated',
      title: status === 'published' ? 'Avis publié' : 'Avis modéré',
      description: status === 'published'
        ? `Avis #${id} publié : le cache catalogue et rapports est invalidé.`
        : `Avis #${id} — statut : ${status}.`,
      actorUserId: moderatorUserId ?? null,
    });
  }
}

/** Réponse du professionnel à un avis. */
export async function replyToReview(id: number, replyBody: string, replyBy?: number | null): Promise<void> {
  const { error } = await supabase
    .from('core_reviews')
    .update({ reply_body: replyBody, reply_by: replyBy ?? null, reply_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function markReviewHelpful(id: number, current: number): Promise<void> {
  const { error } = await supabase.from('core_reviews').update({ helpful_count: (current || 0) + 1 }).eq('id', id);
  if (error) throw error;
}

export async function deleteReview(id: number): Promise<void> {
  await supabase.from('core_review_reports').delete().eq('review_id', id);
  const { error } = await supabase.from('core_reviews').delete().eq('id', id);
  if (error) throw error;
}

/** Signale un avis. */
export async function reportReview(input: {
  reviewId: number;
  organizationId: number;
  reporterUserId?: number | null;
  reporterName?: string | null;
  reason: string;
  detail?: string | null;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_review_reports')
    .insert({
      review_id: input.reviewId,
      organization_id: input.organizationId,
      reporter_user_id: input.reporterUserId ?? null,
      reporter_name: input.reporterName ?? null,
      reason: input.reason,
      detail: input.detail || null,
      status: 'open',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  // Incrémente le compteur de signalements de l'avis.
  try {
    const { data: rev } = await supabase.from('core_reviews').select('report_count').eq('id', input.reviewId).maybeSingle();
    await supabase.from('core_reviews').update({ report_count: ((rev?.report_count as number) || 0) + 1 }).eq('id', input.reviewId);
  } catch {
    /* best-effort */
  }
  return (data?.id as number) ?? null;
}

export async function listReports(organizationId: number): Promise<CoreReviewReport[]> {
  const { data, error } = await supabase
    .from('core_review_reports')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data || []) as CoreReviewReport[];
}

export async function resolveReport(id: number, status: 'resolved' | 'dismissed'): Promise<void> {
  const { error } = await supabase.from('core_review_reports').update({ status }).eq('id', id);
  if (error) throw error;
}

export interface ReviewStats {
  total: number;
  published: number;
  pending: number;
  rejected: number;
  reports: number;
  average: number;
  byRating: Record<number, number>;
}

export async function fetchReviewStats(organizationId: number): Promise<ReviewStats> {
  const [reviews, reports] = await Promise.all([listReviews(organizationId, { limit: 1000 }), listReports(organizationId)]);
  const byRating: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let sum = 0;
  let counted = 0;
  let pending = 0;
  let rejected = 0;
  reviews.forEach((r) => {
    const rating = r.rating || 0;
    if (rating >= 1 && rating <= 5) byRating[rating] += 1;
    if (r.status === 'published' && rating > 0) { sum += rating; counted += 1; }
    if (r.status === 'pending') pending += 1;
    if (r.status === 'rejected') rejected += 1;
  });
  return {
    total: reviews.length,
    published: reviews.filter((r) => r.status === 'published').length,
    pending,
    rejected,
    reports: reports.filter((r) => r.status === 'open').length,
    average: counted > 0 ? Math.round((sum / counted) * 10) / 10 : 0,
    byRating,
  };
}

export interface RatingSummary {
  average: number;
  count: number;
}

/** Note moyenne PUBLIÉE d'une entité précise (pour la page publique). */
export async function fetchEntityRating(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<RatingSummary> {
  const { data, error } = await supabase
    .from('core_reviews')
    .select('rating')
    .eq('organization_id', organizationId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId))
    .eq('status', 'published');
  if (error) throw error;
  const rows = (data || []) as { rating: number | null }[];
  const valid = rows.filter((r) => r.rating && r.rating > 0);
  if (valid.length === 0) return { average: 0, count: 0 };
  const sum = valid.reduce((acc, r) => acc + (r.rating || 0), 0);
  return { average: Math.round((sum / valid.length) * 10) / 10, count: valid.length };
}

export interface PublishedReviewFilter {
  entityType?: string;
  entityId?: string | number | null;
  limit?: number;
}

/**
 * Avis PUBLIÉS d'une organisation (filtre d'entité optionnel).
 * C'est la source utilisée par les pages publiques (/business/:slug, produit,
 * service) : seuls les avis modérés et publiés sont exposés.
 */
export async function listPublishedReviews(
  organizationId: number,
  filter?: PublishedReviewFilter,
): Promise<CoreReview[]> {
  let query = supabase
    .from('core_reviews')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('status', 'published')
    .order('helpful_count', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(filter?.limit ?? 60);
  if (filter?.entityType) query = query.eq('entity_type', filter.entityType);
  if (filter?.entityId != null && filter.entityId !== '') query = query.eq('entity_id', String(filter.entityId));
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreReview[];
}

export interface RatingDistribution {
  average: number;
  count: number;
  byRating: Record<number, number>;
}

/** Note moyenne + répartition calculées à partir d'une liste d'avis publiés. */
export function computeRating(reviews: CoreReview[]): RatingDistribution {
  const byRating: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let sum = 0;
  let count = 0;
  reviews.forEach((r) => {
    const rating = r.rating || 0;
    if (rating >= 1 && rating <= 5) byRating[rating] += 1;
    if (rating > 0) {
      sum += rating;
      count += 1;
    }
  });
  return { average: count > 0 ? Math.round((sum / count) * 10) / 10 : 0, count, byRating };
}

/** Date lisible (jj/mm/aaaa) pour l'affichage public des avis. */
export function formatReviewDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}